import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Character, Organization } from '@/lib/types';

import AuthGuard from '@/components/auth-guard';
import {
  EntityVisual,
  SectionCharacterMain,
  SectionDisplay
} from '@/components/entity-display';
import { EntityTimelineStepper } from '@/components/entity-timeline';
import { DeleteConfirmationModal } from '@/components/mui-delete-confirmation-modal';
import { Page } from '@/components/page';
import { useCharacterAccessLayer } from '@/features/characters';
import { removeCharacterFromOrganizations } from '@/features/organizations/utils/membership-sync';
import { ORGANIZATION_HIERARCHY_FEATURE_KEY } from '@/features/organizations/utils/organization-hierarchy-feature';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useSubscription } from '@/hooks/useSubscription';


export const Route = createFileRoute('/characters/view/$id')({
  component: CharacterViewPage,
});

function ViewCharacterPage() {
  const navigate = useNavigate();
  const { id: characterId } = Route.useParams();
  const { novel, updateEntityItem, deleteItem, entityDao } = useNovelRepository();
  const { t } = useTranslation();
  const { hasFeature } = useSubscription();
  const isOrganizationHierarchyEnabled = hasFeature(ORGANIZATION_HIERARCHY_FEATURE_KEY);


  const [timelineEntityData, setTimelineEntityData] = useState<Record<string, unknown> | null>(null);

  const { toast } = useToast();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    character,
    sections: customFieldSections,
    isLoading,
    deleteCharacter,
  } = useCharacterAccessLayer(characterId);

  // Handle timeline data changes
  const handleTimelineDataChange = useCallback((data: Record<string, unknown>) => {
    setTimelineEntityData(data);
  }, []);

  // Current display data (timeline or base)
  const displayData = (timelineEntityData || character || {}) as Character;

  const handleDelete = async () => {
    if (!characterId) return;
    setIsDeleting(true);
    try {
      if (isOrganizationHierarchyEnabled && novel && entityDao) {
        const orgIds = character?.organizationIds || [];
        const organizations = (await Promise.all(
          orgIds.map((orgId) => entityDao.getItem<Organization>(novel.ownerId, novel, 'entities', 'organizations', orgId))
        )).filter(Boolean) as Organization[];

        await removeCharacterFromOrganizations({
          characterId,
          organizations,
          updateEntityItem,
          characterOrganizationIds: orgIds,
          deleteRelationship: (relationshipId) => deleteItem('entities', 'relationships', relationshipId),
        });
      }
      await deleteCharacter(characterId);
      toast({
        title: t('entity_form.characters.delete_success_title'),
        description: t('entity_form.characters.delete_success_desc'),
      });
      navigate({ to: '/characters' });
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: t('entity_form.common.delete_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
      setIsDeleting(false);
    }
  };



  // Loading state
  if (isLoading) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Box>
    );
  }

  // Not Found
  if (!character) {
    if (novel) {
      return (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h3" color="text.secondary">Character not found</Typography>
            <Typography variant="body1">This persona could not be identified in the selected records.</Typography>
            <Button onClick={() => navigate({ to: '/characters' })} sx={{ mt: 2 }}>Back to Characters</Button>
          </Box>
      );
    }

    // Waiting for novel context
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Box>
    );
  }
  return (
      <Page
        title={character.name || t('entity_view.characters.view_fallback')}
        breadcrumbs={[
          { href: '/characters', label: t('entity_list.characters.title') },
          { label: character.name || t('entity_view.characters.view_fallback') }
        ]}
        layoutMode="scrolling"
        actions={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting}
              sx={{ borderRadius: 2 }}
            >
              {t('entity_form.common.delete')}
            </Button>
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => navigate({ to: '/characters/edit/$id', params: { id: characterId } })}
              sx={{ borderRadius: 2 }}
            >
              {t('entity_view.characters.edit')}
            </Button>
          </Stack>
        }
      >
        <Grid container spacing={4}>
          {/* Main Content Area (70%) */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Stack spacing={4}>
              {/* Basic Identity Section */}
              <SectionCharacterMain data={displayData} />

              {/* Timeline Section */}
              {novel && characterId && character && (
                <EntityTimelineStepper
                  currentEntityData={character}
                  onEntityDataChange={handleTimelineDataChange}
                />
              )}

              {/* Custom Fields Sections */}
              {customFieldSections.map((section) => (
                <SectionDisplay
                  key={section.id}
                  title={section.title}
                  fields={section.fields}
                  data={displayData}
                />
              ))}
            </Stack>
          </Grid>

          {/* Sidebar Area (30%) */}
          <EntityVisual
            title={t('entity_view.characters.visual')}
            images={displayData.images}
            primaryImageId={displayData.primaryImageId}
            altText={character.name || 'Character'}
          />
        </Grid>

        <DeleteConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          isDeleting={isDeleting}
          itemName={character?.name}
          description={t('entity_form.characters.delete_desc')}
        />
      </Page>
  );
}

function CharacterViewPage() {
  return (
    <AuthGuard>
      <Suspense fallback={
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Box>
      }>
        <ViewCharacterPage />
      </Suspense>
    </AuthGuard>
  );
}
